// LengthBump — visitor-side answer-style picker (Feb 14, 2026).
// Bump Studio drawer in matching style (left side, top slot by default).
// Visitors pick how long the AI's answers should be. The choice sticks
// for the current conversation (persisted on chat_sessions server-side).
// New conversations reset to the silo's creator-set default.

import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Phosphor from "@phosphor-icons/react";
import { Check } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useBumpConfig, slotToTopPct, COLOR_SLOT_VAR, SHAPE_CLASS } from "@/lib/bumpConfig";
import { useTabsOff, registerHiddenTabs, useOpenTabListener } from "@/lib/tabToggle";

// =====================================================================
// Public surface
// =====================================================================
export default function LengthBump({ silo, sessionId, activePreset, activeCustom, onChange, viewerUser }) {
  const { cfg: bumpCfg, viewport } = useBumpConfig();
  const sideCfg = bumpCfg ? (bumpCfg.left?.[viewport] || { bumps: [], max_slots: 8 }) : null;
  const myCfg = useMemo(
    () => (sideCfg?.bumps || []).find((b) => b.id === "length") || null,
    [sideCfg],
  );

  // May 25, 2026 — only the silo owner / collaborators see the "Save as
  // default" affordance. Anonymous fans + non-owner subscribers don't.
  const isOwnerOrCollab = !!viewerUser && (
    silo?.owner_user_id === viewerUser.id
    || (silo?.collaborator_ids || []).includes(viewerUser.id)
  );

  const [open, setOpen] = useState(false);
  const [styles, setStyles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);
  // Jul 2026 (Randy) — "Turn off tabs" support.
  const tabsOff = useTabsOff();
  useEffect(() => {
    if (!myCfg) return undefined;
    return registerHiddenTabs("length-bump", [{ key: "left-length", label: myCfg.name || "Length" }]);
  }, [myCfg]);
  useOpenTabListener((key) => { if (key === "left-length") setOpen(true); });

  // Fetch the silo's available styles (built-ins + custom + default) when
  // the drawer opens for the first time, OR whenever the session_id changes
  // (e.g. visitor loads a different bookmark).
  const loadStyles = async () => {
    if (!silo?.slug) return;
    setLoading(true);
    try {
      const r = await api.get(`/s/${silo.slug}/answer-styles${sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ""}`);
      setStyles(r.data);
      // Inform parent of the actively-resolved style so the indicator
      // pill stays in sync without an extra round-trip.
      onChange?.({ preset: r.data.active_preset, custom: r.data.active_custom, source: "initial" });
    } catch (e) {
      // Silent — visitor isn't blocked. Frontend just won't show the shelf.
      // eslint-disable-next-line no-console
      console.warn("answer-styles fetch failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStyles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [silo?.slug, sessionId]);

  if (!myCfg || !silo) return null;

  const pickStyle = (style) => {
    // Frontend-only update — the persist-to-session happens server-side on
    // the next /ask call (we ship `answer_style_preset` + `custom` in the
    // payload). Parent owns that state.
    onChange?.({ preset: style.id, custom: style.is_custom ? { name: style.name, min_words: style.min_words, max_words: style.max_words } : null, source: "user" });
    setOpen(false);
  };

  // May 25, 2026 — owner-only "Save as default" action. Persists the
  // current preset to the silo via PATCH /silos/<id>. After success, the
  // local styles state is refetched so the "Creator's default" badge
  // moves to the right item.
  const saveAsDefault = async () => {
    if (!silo?.id || !activePreset || activePreset === "custom") return;
    setSavingDefault(true);
    try {
      await api.patch(`/silos/${silo.id}`, { answer_style_preset: activePreset });
      // refetch styles so the indicator updates
      await loadStyles();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("save-as-default failed", e);
    } finally {
      setSavingDefault(false);
    }
  };

  return (
    <>
      {!tabsOff && (
      <BumpTab
        side="left"
        open={open}
        onOpen={() => setOpen((v) => !v)}
        cfg={myCfg}
        maxSlots={sideCfg?.max_slots}
        testid="left-bump-length-tab"
      />)}
      <Drawer
        open={open}
        side="left"
        cfg={myCfg}
        onClose={() => setOpen(false)}
        title={myCfg.name || "Length"}
        testid="left-bump-length-drawer"
      >
        <LengthDrawerContents
          styles={styles}
          loading={loading}
          activePreset={activePreset}
          activeCustom={activeCustom}
          onPick={pickStyle}
          isOwnerOrCollab={isOwnerOrCollab}
          onSaveDefault={saveAsDefault}
          savingDefault={savingDefault}
        />
      </Drawer>
    </>
  );
}

// =====================================================================
// Drawer body — header + FAQ + preset list
// =====================================================================
function LengthDrawerContents({ styles, loading, activePreset, activeCustom, onPick, isOwnerOrCollab, onSaveDefault, savingDefault }) {
  if (loading && !styles) {
    return <div className="text-xs tpl-muted py-6">Loading length options…</div>;
  }
  if (!styles) return null;

  const items = styles.items || [];
  const currentIsAlreadyDefault = activePreset === styles.silo_default_preset;

  return (
    <div className="space-y-5" data-testid="length-bump-contents">
      {/* Headline */}
      <div>
        <p className="text-[11px] uppercase tracking-[0.18em] tpl-muted mb-1">How long should the AI answer?</p>
        <p className="text-sm leading-relaxed" style={{ color: "var(--tpl-text)" }}>
          Pick a length below. The AI keeps all its thinking power either way — this just shapes how long the written answer comes out.
        </p>
      </div>

      {/* Style list */}
      <div className="space-y-2" data-testid="length-bump-options">
        {items.map((it) => {
          const isActive = activePreset === it.id && (
            it.id !== "custom" || (activeCustom?.name && activeCustom.name === it.name)
          );
          const isDefault = it.id === styles.silo_default_preset;
          return (
            <button
              key={`${it.id}-${it.name}`}
              type="button"
              onClick={() => onPick(it)}
              data-testid={`length-option-${it.id}`}
              className={`w-full text-left p-3 rounded-md border transition-all flex items-start gap-3 hover:shadow-sm ${
                isActive
                  ? "border-[var(--tpl-accent)] bg-[var(--tpl-accent)]/10"
                  : "tpl-border hover:border-[var(--tpl-accent)]"
              }`}
              style={{ background: isActive ? undefined : "var(--tpl-surface)" }}
            >
              <div className="mt-0.5 shrink-0">
                {isActive ? (
                  <Check size={16} weight="bold" className="text-[var(--tpl-accent)]" />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 tpl-border" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-medium" style={{ color: "var(--tpl-text)" }}>
                    {it.name}
                  </span>
                  {isDefault && (
                    <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded tpl-muted border tpl-border">
                      Creator's default
                    </span>
                  )}
                  {it.is_custom && !isDefault && (
                    <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded text-[var(--tpl-accent)] border border-[var(--tpl-accent)]/40">
                      Custom
                    </span>
                  )}
                </div>
                <p className="text-[11px] tpl-muted mt-0.5 leading-snug">{it.description}</p>
              </div>
            </button>
          );
        })}
      </div>
      {/* May 25, 2026 — Owner-only "Save as default" affordance. Without
          this, picking a length only affects the current session; new
          conversations reset to whatever was previously saved as the silo
          default. Tap save and the current pick becomes permanent for all
          new visitors. */}
      {isOwnerOrCollab && activePreset && activePreset !== "custom" && (
        <div className="pt-1 border-t hairline" data-testid="length-save-default-section">
          {currentIsAlreadyDefault ? (
            <p className="text-[11px] tpl-muted text-center pt-3">
              This is the saved default for new conversations.
            </p>
          ) : (
            <button
              type="button"
              onClick={onSaveDefault}
              disabled={savingDefault}
              data-testid="length-save-default-btn"
              className="w-full text-xs uppercase tracking-[0.16em] py-2 mt-3 rounded-md border tpl-border hover:border-[var(--tpl-accent)] hover:text-[var(--tpl-accent)] disabled:opacity-50"
              style={{ color: "var(--tpl-text)" }}
            >
              {savingDefault ? "Saving…" : "Save as default for everyone"}
            </button>
          )}
          <p className="text-[10px] tpl-muted text-center mt-1.5">
            Only you and your collaborators see this option.
          </p>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// BumpTab + Drawer — visual style identical to SideBumps (kept local so
// LengthBump is self-contained; if SideBumps later exports them, swap.)
// =====================================================================
function BumpTab({ side, open, onOpen, cfg, maxSlots, testid }) {
  const top = slotToTopPct(cfg.slot, maxSlots);
  const shapeClass = SHAPE_CLASS[cfg.shape] || "rounded-r-md border-l-0 shadow-md";
  const colors = COLOR_SLOT_VAR[cfg.color_slot] || COLOR_SLOT_VAR.surface;
  const iconKey = cfg.icon || "TextAa";
  const showIcon = iconKey !== "None";
  const PhosIcon = Phosphor[iconKey] || Phosphor.TextAa;
  const displayLabel = cfg.name ?? "";
  const textSize = cfg.text_size || 10;

  return (
    <button
      onClick={onOpen}
      data-testid={testid}
      aria-expanded={open}
      className={`fixed left-0 z-30 flex flex-col items-center justify-center gap-1.5 border tpl-border transition-colors hover:tpl-accent ${shapeClass}`}
      style={{
        top,
        color: colors.fg,
        background: colors.bg,
        width: `${cfg.width}px`,
        height: `${cfg.height}px`,
      }}
    >
      {showIcon && <PhosIcon size={Math.max(12, textSize + 4)} weight="duotone" />}
      {displayLabel && (
        <span
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: `${textSize}px` }}
          className="uppercase tracking-[0.18em] font-medium whitespace-nowrap overflow-hidden"
        >
          {displayLabel}
        </span>
      )}
    </button>
  );
}

function Drawer({ open, side, onClose, testid, title, cfg, children }) {
  const widthStyle = cfg?.drawer_width ? { width: `${cfg.drawer_width}px` } : undefined;
  const topPct = cfg?.drawer_top_pct ?? 0;
  const bottomPct = cfg?.drawer_bottom_pct ?? 0;
  const insetStyle = (topPct || bottomPct)
    ? { ...(widthStyle || {}), top: `${topPct}vh`, bottom: `${bottomPct}vh` }
    : widthStyle;
  const insetClass = (topPct || bottomPct) ? "" : "top-0 bottom-0";
  const drawerStyle = cfg?.drawer_style || "slide-rect";
  const STYLE_CLASSES = {
    "slide-rect": "tpl-surface border-l border-r tpl-border shadow-2xl",
    "slide-rounded": "tpl-surface border tpl-border shadow-2xl rounded-2xl",
    "slide-card": "tpl-surface border tpl-border shadow-2xl rounded-r-2xl ml-1 my-2",
    "slide-frosted": "border tpl-border shadow-2xl backdrop-blur-md bg-white/70",
    "slide-edgeless": "tpl-surface shadow-xl",
    "slide-tabbed": "tpl-surface border-l-4 border-r tpl-border shadow-2xl [border-left-color:var(--tpl-accent)]",
  };
  const styleClasses = STYLE_CLASSES[drawerStyle] || STYLE_CLASSES["slide-rect"];

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            className="fixed inset-0 z-[79] bg-transparent"
            data-testid={`${testid}-backdrop`}
          />
          <motion.div
            initial={{ x: -320, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -320, opacity: 0 }}
            transition={{ type: "tween", duration: 0.22 }}
            onClick={onClose}
            className={`fixed ${insetClass} left-0 z-[80] ${styleClasses} flex flex-col cursor-pointer`}
            style={insetStyle}
            data-testid={testid}
          >
            <div className="flex items-center px-4 py-3 border-b tpl-border">
              <div className="text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--tpl-text)" }}>
                {title}
              </div>
              <span className="ml-auto text-[10px] uppercase tracking-[0.2em] tpl-muted opacity-60">
                Tap anywhere to close
              </span>
            </div>
            <div
              className="flex-1 overflow-y-auto p-4 cursor-default"
              onClick={(e) => {
                if (e.target.closest("button, a, input, textarea, label")) {
                  e.stopPropagation();
                }
              }}
            >
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
